/**
 * Workflow Configuration Utilities
 *
 * Pure functions for processing and formatting workflow configuration data.
 * Used for Workflow and WorkflowActivity entities (Settings property).
 */

/**
 * Safely parse JSON strings and clone objects to avoid mutating the original
 */
export function cloneJsonValue(value: any): any {
    if (value === null || value === undefined) return value;

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === '{}') {
            return {};
        }
        try {
            return JSON.parse(trimmed);
        } catch {
            // Return as-is if parsing fails
            return value;
        }
    }

    // Deep clone objects/arrays to avoid mutating original entity data
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return value;
    }
}

/**
 * Allowed keys when lifting a step into workflow Settings.zSteps.
 * Only these are stored; _ and $ properties (e.g. $debug, _attributesRequested) are never captured.
 */
const ZSTEPS_ALLOWED_KEYS = ['Id', 'DisplayName', 'Settings', 'Script'] as const;

/**
 * Build the object to store in Settings.zSteps[id] when importing a step into a workflow.
 * Only Id, DisplayName, Settings, and Script are captured; _ and $ properties are stripped.
 */
export function stepForZSteps(step: any): Record<string, unknown> {
    if (step == null || typeof step !== 'object') return {};
    const out: Record<string, unknown> = {};
    for (const k of ZSTEPS_ALLOWED_KEYS) {
        const v = step[k];
        if (v !== undefined) out[k] = v;
    }
    return out;
}

/**
 * Process a single workflow step, parsing nested Settings (step-level, for workflow activity copies in zSteps).
 */
function processWorkflowStep(stepValue: any): any {
    if (stepValue === null || stepValue === undefined) {
        return stepValue;
    }

    let stepClone: any;
    try {
        stepClone = typeof stepValue === 'string'
            ? cloneJsonValue(stepValue)
            : JSON.parse(JSON.stringify(stepValue));
    } catch {
        stepClone = stepValue;
    }

    const stepSettingsVal = stepClone?.Settings;
    if (stepClone && typeof stepClone === 'object' && stepSettingsVal !== undefined && stepSettingsVal !== null) {
        try {
            const parsed = cloneJsonValue(stepSettingsVal);
            stepClone.Settings = parsed;
        } catch {
            // Leave as-is if parsing fails
        }
    }

    return stepClone;
}

/**
 * Unwind workflow Settings so nested zSteps Settings values are parsed (unescaped).
 */
export function unwindWorkflowConfig(configValue: any): any {
    if (!configValue || typeof configValue !== 'object') {
        return configValue;
    }

    const configClone = JSON.parse(JSON.stringify(configValue));

    if (configClone.zSteps) {
        if (Array.isArray(configClone.zSteps)) {
            configClone.zSteps = configClone.zSteps.map(processWorkflowStep);
        } else if (typeof configClone.zSteps === 'object') {
            const newZSteps: Record<string, any> = {};
            Object.entries(configClone.zSteps).forEach(([key, step]) => {
                newZSteps[key] = processWorkflowStep(step);
            });
            configClone.zSteps = newZSteps;
        }
    }

    return configClone;
}

/**
 * Format workflow configuration for display (pretty-printed JSON)
 */
export function formatWorkflowConfigForDisplay(value: any): string {
    try {
        const baseConfig = cloneJsonValue(value);
        const unwoundConfig = unwindWorkflowConfig(baseConfig);
        return JSON.stringify(unwoundConfig, null, 2);
    } catch {
        if (typeof value === 'string') {
            return value;
        }
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return '{}';
        }
    }
}

/**
 * Detect when a JSON-type field (e.g. Settings) was returned as a string by the API instead of an object.
 * Use this to show a warning that we will display and send it as JSON.
 */
export function isJsonFieldReturnedAsString(value: any, fieldName?: string): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '{}' || trimmed === '[]') return false;
    const isConfigField = fieldName === 'Settings' || fieldName === 'settings';
    const looksLikeJson = (trimmed.startsWith('{') && trimmed.includes('}')) || (trimmed.startsWith('[') && trimmed.includes(']'));
    return isConfigField || looksLikeJson;
}

/** Message shown when the API returned a JSON-type field as a string. */
export const JSON_FIELD_STRING_WARNING_MESSAGE =
    'This field was returned as a string by the API. It is displayed and will be saved as JSON.';

/**
 * Check if a value is a valid JSON string (object or array, not primitives)
 */
export function isValidJson(value: string): boolean {
    if (!value || typeof value !== 'string' || value.trim() === '') {
        return false;
    }
    try {
        const parsed = JSON.parse(value);
        // Only consider it JSON if it's an object or array, not a primitive (number, string, boolean, null)
        return typeof parsed === 'object' && parsed !== null;
    } catch {
        return false;
    }
}

/**
 * Safely stringify a value for display
 */
export function safeStringify(value: any, indent: number = 2): string {
    try {
        return JSON.stringify(value, null, indent);
    } catch {
        return String(value);
    }
}

/**
 * Parse a JSON string safely, returning the original on failure
 */
export function safeParse<T = any>(value: string, defaultValue?: T): T {
    try {
        return JSON.parse(value);
    } catch {
        return (defaultValue !== undefined ? defaultValue : value) as T;
    }
}

/**
 * Produce a canonical JSON string (sorted keys at every level) so two semantically
 * equal values compare equal regardless of key order. Used for change detection.
 */
export function canonicalJsonString(value: string | object): string {
    let obj: any;
    if (typeof value === 'string') {
        const s = value.trim();
        if (s === '' || s === '{}') return '{}';
        try {
            obj = JSON.parse(s);
        } catch {
            return typeof value === 'string' ? value : JSON.stringify(value);
        }
    } else {
        obj = value;
    }
    if (obj === null || typeof obj !== 'object') {
        return JSON.stringify(obj);
    }
    function canonicalize(val: any): any {
        if (val === null || typeof val !== 'object') return val;
        if (Array.isArray(val)) return val.map(canonicalize);
        const sorted: Record<string, any> = {};
        for (const k of Object.keys(val).sort()) {
            sorted[k] = canonicalize(val[k]);
        }
        return sorted;
    }
    return JSON.stringify(canonicalize(obj));
}

/**
 * Normalize workflow Settings string before sending in a PATCH/PUT.
 * Removes extra escaping; normalizes nested zSteps[].Settings the same way.
 */
export function normalizeWorkflowConfigForPatch(configValue: string): string {
    if (configValue == null || typeof configValue !== 'string') return configValue ?? '';
    let s = configValue.trim();
    if (s === '') return '{}';

    let parsed: any;
    try {
        parsed = JSON.parse(s);
    } catch {
        return configValue;
    }

    // If the top level is a string, it's likely double-encoded; unwrap until we get an object
    while (typeof parsed === 'string') {
        const inner = parsed.trim();
        if (inner === '' || inner === '{}') return '{}';
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return JSON.stringify(parsed);
        }
    }

    if (typeof parsed !== 'object' || parsed === null) {
        return configValue;
    }

    // Normalize nested zSteps[*].Settings the same way
    if (parsed.zSteps && typeof parsed.zSteps === 'object') {
        if (Array.isArray(parsed.zSteps)) {
            parsed.zSteps = parsed.zSteps.map((step: any) => normalizeStepSettingsForPatch(step));
        } else {
            const out: Record<string, any> = {};
            for (const [k, v] of Object.entries(parsed.zSteps)) {
                out[k] = normalizeStepSettingsForPatch(v);
            }
            parsed.zSteps = out;
        }
    }

    return JSON.stringify(parsed);
}

function normalizeStepSettingsForPatch(step: any): any {
    if (step == null || typeof step !== 'object') return step;
    const stepCopy = { ...step };
    if (typeof stepCopy.Settings === 'string' && stepCopy.Settings.trim() !== '') {
        try {
            stepCopy.Settings = normalizeWorkflowConfigForPatch(stepCopy.Settings);
        } catch {
            // leave as-is
        }
    }
    return stepCopy;
}

/**
 * Return workflow Settings as a JSON object for PATCH/PUT so the API receives actual JSON, not a string.
 * Parses nested zSteps[].Settings to objects.
 */
export function getWorkflowConfigPayload(configValue: string): Record<string, any> {
    const normalizedStr = normalizeWorkflowConfigForPatch(configValue ?? '');
    if (normalizedStr.trim() === '' || normalizedStr.trim() === '{}') return {};
    try {
        const obj = JSON.parse(normalizedStr) as Record<string, any>;
        if (!obj || typeof obj !== 'object') return {};
        if (obj.zSteps && typeof obj.zSteps === 'object') {
            if (Array.isArray(obj.zSteps)) {
                obj.zSteps = obj.zSteps.map((step: any) => stepSettingsToObject(step));
            } else {
                const out: Record<string, any> = {};
                for (const [k, v] of Object.entries(obj.zSteps)) {
                    out[k] = stepSettingsToObject(v);
                }
                obj.zSteps = out;
            }
        }
        return obj;
    } catch {
        return {};
    }
}

function stepSettingsToObject(step: any): any {
    if (step == null || typeof step !== 'object') return step;
    const stepCopy = { ...step };
    if (typeof stepCopy.Settings === 'string' && stepCopy.Settings.trim() !== '') {
        try {
            const normalized = normalizeWorkflowConfigForPatch(stepCopy.Settings);
            stepCopy.Settings = JSON.parse(normalized);
        } catch {
            // leave as string if parse fails
        }
    }
    return stepCopy;
}

/**
 * Translate step-level Config to Settings in a payload (for backward compat with old data).
 */
export function translateWorkflowConfigStepsToSettings(payload: Record<string, any>): Record<string, any> {
    if (!payload || typeof payload !== 'object') return payload;
    const out = JSON.parse(JSON.stringify(payload));

    const translateStep = (step: any): any => {
        if (step == null || typeof step !== 'object') return step;
        const s = { ...step };
        if ((s as any).Config !== undefined) {
            (s as any).Settings = (s as any).Config;
            delete (s as any).Config;
        }
        if ((s as any).config !== undefined) {
            (s as any).settings = (s as any).config;
            delete (s as any).config;
        }
        return s;
    };

    if (out.zSteps && typeof out.zSteps === 'object') {
        if (Array.isArray(out.zSteps)) {
            out.zSteps = out.zSteps.map(translateStep);
        } else {
            const z: Record<string, any> = {};
            for (const [k, v] of Object.entries(out.zSteps)) {
                z[k] = translateStep(v);
            }
            out.zSteps = z;
        }
    }
    if (Array.isArray(out.steps)) {
        out.steps = out.steps.map(translateStep);
    }
    return out;
}

/**
 * Unescape a JSON-encoded string (handles Body property in EmailTemplates)
 */
export function unescapeJsonString(value: string): string {
    if (!value || typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
        try {
            const parsed = JSON.parse(value);
            if (typeof parsed === 'string') {
                return parsed;
            }
        } catch {
            // Not a JSON-escaped string, keep as-is
        }
    }

    return value;
}

/**
 * Detect the syntax type for code highlighting based on content
 */
export function detectSyntaxType(value: string, fieldName?: string): 'json' | 'sql' | 'javascript' | 'html' | 'text' {
    const lowerFieldName = fieldName?.toLowerCase() || '';

    // Check field name hints first
    if (lowerFieldName.includes('sql')) return 'sql';
    if (lowerFieldName === 'script') return 'javascript';
    if (lowerFieldName === 'body') return 'html';
    if (lowerFieldName === 'config' || lowerFieldName === 'settings') return 'json';

    // Try to detect from content
    if (!value || typeof value !== 'string') return 'text';

    const trimmed = value.trim();

    // Check for JSON
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        if (isValidJson(trimmed)) return 'json';
    }

    // Check for SQL keywords
    const sqlKeywords = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'FROM', 'WHERE', 'JOIN'];
    const upperValue = trimmed.toUpperCase();
    if (sqlKeywords.some(keyword => upperValue.includes(keyword))) {
        return 'sql';
    }

    // Check for HTML
    if (trimmed.includes('<') && trimmed.includes('>') &&
        (trimmed.includes('</') || trimmed.includes('/>'))) {
        return 'html';
    }

    // Check for JavaScript patterns
    if (trimmed.includes('function') || trimmed.includes('=>') ||
        trimmed.includes('const ') || trimmed.includes('let ') ||
        trimmed.includes('var ')) {
        return 'javascript';
    }

    return 'text';
}

